import store from '@/store';
import { Money } from '@libs/money/money.interfaces';
import { ProductRow, ProductRowItem } from '../types';
import { mapFlightLabel, mapFlightPrice, mapFlightSubLabel } from './flight';
import { mapHotelLabel, mapHotelPrice, mapHotelSubLabel } from './hotel';
import { mapActivityPrice, mapActivitySubLabel } from './activity';
import { mapTransferLabel, mapTransferSubLabel, mapTransferPrice } from './transfer';
import {
    ActivityProduct,
    AddonProduct,
    FerryProduct,
    FlightProduct,
    HotelProduct,
    Product,
    ProductType,
    FerryAddonsProduct,
    TransferProduct,
    SerializationType,
} from '@/api/Cart/cart.interfaces';
import { AnyProduct } from '@shared/interfaces/product';
import { Dictionary } from '@shared/interfaces/dictionary';
import { mapAddonLabel } from './addon';
import { GROUP_BY_COUNT, COMBINE_ALLOWED } from '../consts';
import { mapGolfPrice, mapGolfSubLabel } from './golf';
import { mapFerryPrice, mapFerrySubLabel } from './ferry';
import { t } from '@shared/helpers/translation';

const mapProduct = (product: AnyProduct): ProductRowItem => {
    return {
        price: getPrice(product),
        label: getLabel(product),
        subLabel: getSubLabel(product),
        title: getTitle(product.Type),
        product,
    };
};

const getLabel = (product: Product): string => {
    switch (product.Type) {
        case ProductType.Flight:
            return mapFlightLabel(product as FlightProduct);
        case ProductType.Hotel:
            return mapHotelLabel(product as HotelProduct);
        case ProductType.Addon:
            return mapAddonLabel(product as AddonProduct);
        case ProductType.Transfer:
            if (isValidTransferProduct(product)) {
                return mapTransferLabel(product as TransferProduct);
            }
            return '';
        default:
            return product.Description;
    }
};

const getSubLabel = (product: Product): string | undefined => {
    switch (product.Type) {
        case ProductType.Flight:
            return mapFlightSubLabel(product as FlightProduct);
        case ProductType.Ferry:
            return mapFerrySubLabel(product as FerryProduct);
        case ProductType.Hotel:
            return mapHotelSubLabel(product as HotelProduct);
        case ProductType.Golf:
            return mapGolfSubLabel(product as HotelProduct);
        case ProductType.Activity:
            return mapActivitySubLabel(product as ActivityProduct);
        case ProductType.Transfer:
            return mapTransferSubLabel(product as TransferProduct);
        default:
            return undefined;
    }
};

export const getTitle = (type: ProductType, count = 1): string | undefined => {
    switch (type) {
        case ProductType.Flight:
            return t({
                key: 'shared.flight_product',
                keyPlural: 'shared.flight_products',
                count: count,
            });
        case ProductType.Ferry:
            return t({
                key: 'shared.ferry_product',
                keyPlural: 'shared.ferry_products',
                count: count,
            });
        case ProductType.Hotel:
            return t({
                key: 'shared.hotel_product',
                keyPlural: 'shared.hotel_products',
                count: count,
            });
        case ProductType.Golf:
            return t({
                key: 'shared.golf_product',
                keyPlural: 'shared.golf_products',
                count: count,
            });
        case ProductType.Activity:
            return t({
                key: 'shared.activity_product',
                keyPlural: 'shared.activity_products',
                count: count,
            });
        case ProductType.Transfer:
            return t({
                key: 'shared.transfer_product',
                keyPlural: 'shared.transfer_products',
                count: count,
            });
        default:
            return undefined;
    }
};

const getPrice = (product: AnyProduct): Money => {
    switch (product.Type) {
        case ProductType.Flight:
            return mapFlightPrice(product as FlightProduct);
        case ProductType.Ferry:
            return mapFerryPrice(product as FerryProduct);
        case ProductType.Hotel:
            return mapHotelPrice(product as HotelProduct);
        case ProductType.Golf:
            return mapGolfPrice(product as HotelProduct);
        case ProductType.Activity:
            return mapActivityPrice(product as ActivityProduct);
        case ProductType.Transfer:
            return mapTransferPrice(product as TransferProduct);
        default:
            return product.Price;
    }
};

const getTotalPrice = (items: ProductRowItem[]): Money => {
    const total = items
        .map((x) => x.price.Amount)
        .reduce((sum: number, amount) => (sum += amount), 0);

    return {
        Amount: total,
        Currency: items[0].price.Currency,
    };
};

export const getProducts = (combine: boolean) => {
    const items: ProductRow[] = [];

    const products = store.getters['cart/getProducts'];

    if (products && products?.length === 0) {
        return [];
    }

    const groups = getGroupsOfProducts(products);
    const groupCount = getGroupProductsCount(groups);

    Object.keys(groups).map((key) => {
        const productItems = flattenChildProducts(groups[key] as AnyProduct[]);

        const shouldCombine =
            groupCount > GROUP_BY_COUNT &&
            combine !== false &&
            COMBINE_ALLOWED.indexOf(key) > -1;

        if (shouldCombine) {
            const rowItems = productItems.map((item) => mapProduct(item));

            items.push({
                items: rowItems,
                combine: true,
                totalPrice: getTotalPrice(rowItems),
                type: productItems[0].Type,
            });
        } else {
            productItems.forEach((item) => {
                items.push({
                    items: [mapProduct(item)],
                    combine: false,
                    type: item.Type,
                });
            });
        }
    });

    return items;
};

const flattenChildProducts = (products: AnyProduct[]) => {
    const childProducts = (
        products.filter((p) => p.Type === ProductType.FerryAddons) as FerryAddonsProduct[]
    ).flatMap((p) => p.Addons);

    return products.filter((p) => p.Type !== ProductType.FerryAddons).concat(childProducts);
};

const getGroupProductsCount = (groups: Dictionary<AnyProduct[]>) => {
    return Object.keys(groups)
        .filter((key) => COMBINE_ALLOWED.indexOf(key) > -1)
        .reduce((count, key) => (count += groups[key].length), 0);
};

const getGroupsOfProducts = (products: AnyProduct[]): Dictionary<AnyProduct[]> => {
    return products.reduce(
        (productTypeObj: Dictionary<AnyProduct[]>, currentProduct: AnyProduct) => {
            productTypeObj[currentProduct.Type] = [
                ...(productTypeObj[currentProduct.Type] || []),
                currentProduct,
            ];

            return productTypeObj;
        },
        {}
    );
};

const isValidTransferProduct = (product: Product) => {
    return (
        product.Type === ProductType.Transfer &&
        product.$type === SerializationType.TransferProduct
    );
};
